// WHAT: Filter bar component for filtering and searching roadmap items
// WHY:  Provides comprehensive filtering options (type, status, module, search) and show/hide completed toggle
// DOMAIN: Admin → Roadmap → Overview
// DEPENDENCIES: useRoadmapContext hook, UI components (Card, Input, Select, Button, Badge, Stack)
// RISKS:
//   - NOTE: Module filter uses includes() - partial matches may be unexpected
//   - NOTE: Search filter is case-insensitive but may match unintended content
//   - NOTE: Filter state is managed in context - changes affect all components using context

import {
  LucideFilter,
  LucideX,
  LucideEye,
  LucideEyeOff,
  LucideCheckCircle,
  LucideSearch,
} from "lucide-react";
import React from "react";

import { Card, Input, Select, Button, Badge, Stack } from "@/components/ui";
import { useRoadmapContext } from "@/domains/admin/roadmap/context";

/* ------------------------------------------------------------ */
/* Show Completed Toggle Component                               */
/* ------------------------------------------------------------ */

/**
 * Props for ShowCompletedToggle component
 */
interface ShowCompletedToggleProperties {
  /** Number of completed items */
  completedCount: number;
  /** Callback to toggle show/hide completed items */
  onToggle: () => void;
  /** Whether completed items are currently shown */
  showCompleted: boolean;
}

/**
 * WHAT:
 *   Toggle button component for showing/hiding completed roadmap items.
 *
 * WHY:
 *   Provides quick access to toggle completed items visibility with visual
 *   indication of completed count.
 *
 * BEHAVIOR:
 *   - Displays eye icon (open/closed) based on state
 *   - Shows completed count badge when count > 0
 *   - Updates filter context on toggle
 *
 * @param root0
 *
 * @param root0.showCompleted
 *
 * @param root0.onToggle
 *
 * @param root0.completedCount
 *
 * @returns {JSX.Element} Show completed toggle button component
 *
 */
const ShowCompletedToggle: React.FC<ShowCompletedToggleProperties> = ({
  completedCount,
  onToggle,
  showCompleted,
}) => (
  <Button
    className="flex items-center gap-2"
    onClick={onToggle}
    size="sm"
    title={showCompleted ? "Hide completed items" : "Show completed items"}
    variant="outline"
  >
    {showCompleted ? <LucideEye className="w-4 h-4" /> : <LucideEyeOff className="w-4 h-4" />}
    <span>{showCompleted ? "Hide" : "Show"} Completed</span>
    {completedCount > 0 && (
      <Badge size="sm" style="subtle" variant="default">
        {completedCount}
      </Badge>
    )}
  </Button>
);

/* ------------------------------------------------------------ */
/* Main FilterBar Component                                      */
/* ------------------------------------------------------------ */

/**
 * WHAT:
 *   Main filter bar component providing filtering and search capabilities
 *   for roadmap items with active filter display and clear functionality.
 *
 * WHY:
 *   Enables users to quickly filter roadmap items by type, status, module,
 *   and search text, improving navigation in large roadmaps.
 *
 * BEHAVIOR:
 *   - Manages filter state in context
 *   - Provides type, status, module, and search filters
 *   - Shows/hides completed items toggle
 *   - Displays active filters as removable badges
 *   - Calculates completed items count
 *
 * RISKS:
 *   - NOTE: Module filter uses includes() - partial matches may be unexpected
 *   - NOTE: Search filter matches title and description - may return unexpected results
 *   - NOTE: Filter changes affect all components using roadmap context
 *
 * @returns {JSX.Element} Filter bar component
 *
 */
export const FilterBar = () => {
  // NOTE: Get filter state and items from context
  const { filter, items, setFilter } = useRoadmapContext();

  /**
   * WHAT:
   *   Handles filter field changes and updates context.
   *
   * WHY:
   *   Centralizes filter update logic with proper value normalization.
   *
   * BEHAVIOR:
   *   - Sets filter value or undefined if empty
   *   - Updates context filter state
   *
   * @param key
   *
   * @param value
   *
   */
  const handleChange = (key: keyof typeof filter, value: string) => {
    setFilter({ ...filter, [key]: value || undefined });
  };

  // NOTE: Check if any filters are active
  const hasActiveFilters = Object.values(filter).some((value) => value && value !== "");

  // NOTE: Calculate completed items count for toggle badge
  const completedCount = items.filter((item) => item.status === "done").length;

  const typeOptions = [
    { label: "All Types", value: "" },
    { label: "🐛 Bug", value: "bug" },
    { label: "🎯 Feature", value: "feature" },
    { label: "✨ Insight", value: "insight" },
    { label: "💡 Idea", value: "idea" },
  ];

  const statusOptions = [
    { label: "All Status", value: "" },
    { label: "📋 Open", value: "open" },
    { label: "🔄 In Progress", value: "in_progress" },
    { label: "✅ Done", value: "done" },
    { label: "❌ Rejected", value: "rejected" },
  ];

  return (
    <Card className="bg-slate-700/30 border-slate-600/50" padding="lg" variant="default">
      <Stack direction="col" gap={4}>
        {/* Header */}
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={3}>
            <LucideFilter className="w-4 h-4 text-tracq-bright-blue" />
            <h3 className="text-base font-semibold text-white">Filter & Search</h3>
          </Stack>
          {hasActiveFilters ? (
            <Button
              className="flex items-center gap-1.5 text-tracq-deep-orange hover:text-tracq-light-orange"
              onClick={() => {
                setFilter({});
              }}
              size="sm"
              variant="ghost"
            >
              <LucideX className="w-3 h-3" />
              Clear All
            </Button>
          ) : null}
        </Stack>

        {/* Show Completed Toggle */}
        <Stack align="center" direction="row" gap={3}>
          <ShowCompletedToggle
            completedCount={completedCount}
            onToggle={() => {
              setFilter({
                ...filter,
                showCompleted: !(filter.showCompleted ?? false),
              });
            }}
            showCompleted={filter.showCompleted ?? false}
          />
          {completedCount > 0 && !filter.showCompleted && (
            <Stack align="center" className="text-xs text-slate-400" direction="row" gap={2}>
              <LucideCheckCircle className="w-3 h-3" />
              <span>
                {completedCount} completed item{completedCount === 1 ? "" : "s"} hidden
              </span>
            </Stack>
          )}
        </Stack>

        {/* Filter Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Type Filter */}
          <Select
            onChange={(value) => {
              handleChange("type", Array.isArray(value) ? (value[0] ?? "") : value);
            }}
            options={typeOptions}
            placeholder="All Types"
            size="sm"
            value={filter.type ?? ""}
          />

          {/* Status Filter */}
          <Select
            onChange={(value) => {
              handleChange("status", Array.isArray(value) ? (value[0] ?? "") : value);
            }}
            options={statusOptions}
            placeholder="All Status"
            size="sm"
            value={filter.status ?? ""}
          />

          {/* Module Filter */}
          <Input
            onChange={(e) => {
              handleChange("module", e.target.value);
            }}
            placeholder="Module name..."
            size="sm"
            value={filter.module ?? ""}
          />

          {/* Search Filter */}
          <div className="relative">
            <LucideSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="pl-10"
              onChange={(e) => {
                handleChange("search", e.target.value);
              }}
              placeholder="Search items..."
              size="sm"
              value={filter.search ?? ""}
            />
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters ? (
          <div className="pt-3 border-t border-slate-600">
            <Stack align="center" className="flex-wrap" direction="row" gap={2}>
              <span className="text-xs text-slate-400 font-medium">Active filters:</span>
              {Object.entries(filter).map(([key, value]) => {
                if (!value || key === "showCompleted") {
                  return null;
                }
                return (
                  <Badge
                    className="flex items-center gap-1.5"
                    key={key}
                    size="sm"
                    style="solid"
                    variant="primary"
                  >
                    {key}: {value}
                    <button
                      className="hover:text-slate-700 transition-colors"
                      onClick={() => {
                        handleChange(key as keyof typeof filter, "");
                      }}
                    >
                      <LucideX className="w-3 h-3" />
                    </button>
                  </Badge>
                );
              })}
            </Stack>
          </div>
        ) : null}
      </Stack>
    </Card>
  );
};
